/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

const isFolderAlreadyOpenedForField = new Set();

/**
 * Trigger onFilterChange if the corresponding picker folder not been opened
 * @param domRoot
 * @param webComponentTag
 * @param onFilterChange
 * @param pagination
 * @returns {Promise<void>}
 */
const triggerOnFilterChange = async (domRoot, webComponentTag, onFilterChange, pagination) => {
    const pickerId = getPickerId(domRoot, webComponentTag);
    if (pickerId && domRoot?.current?.closest(webComponentTag) && !isFolderAlreadyOpenedForField.has(pickerId)) {
        // Load root category
        await onFilterChange({ pagination: pagination }, null);
    }
};

/**
 * Get Picker Id
 * @param domRoot
 * @param webComponentTag
 * @returns {string|null}
 */
const getPickerId = (domRoot, webComponentTag) => {
    let pickerId = null;
    if (domRoot && webComponentTag && domRoot?.current?.closest(webComponentTag)?.parentElement?.id) {
        pickerId = webComponentTag + '-' + domRoot.current.closest(webComponentTag).parentElement.id;
    }
    return pickerId;
};

/**
 * Assign the corresponding field folder to be opened
 * @param domRoot
 * @param webComponentTag
 */
const assignFolderBeenOpened = (domRoot, webComponentTag) => {
    if (domRoot?.current?.closest(webComponentTag)) {
        isFolderAlreadyOpenedForField.add(getPickerId(domRoot, webComponentTag));
    }
};

/**
 * Triggers the picker component when renders
 * @param domRoot
 * @param webComponentTag
 * @param onFilterChange
 * @param pagination
 */
const triggerWhenPickerRenders = (domRoot, webComponentTag, onFilterChange, pagination) => {
    const pickerId = getPickerId(domRoot, webComponentTag);
    if (pickerId && domRoot?.current?.closest(webComponentTag)) {
        isFolderAlreadyOpenedForField.delete(getPickerId(domRoot, webComponentTag));
    } else {
        onFilterChange({ pagination: pagination }, null);
    }
};

// Export the functions
export {
    triggerOnFilterChange,
    getPickerId,
    assignFolderBeenOpened,
    triggerWhenPickerRenders,
    isFolderAlreadyOpenedForField
};
